import {
  LOINC_DISPLAY_NAMES_ES,
  SURVEY_SCORES_DISPLAY_NAMES_ES,
  getLoincLabDisplayName,
  getTaskLoincCode,
  getTaskResultDisplayNameEs,
} from '@liza/fhir-utils';
import { getLifestyleMeasurementDisplay, getLifestyleMeasurementUnit } from '@liza/medplum-config/valueSet';
import { SURVEY_SCORES_SYSTEM } from '@liza/medplum-config/codeSystem';
import { formatCodeableConcept } from '@medplum/core';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CodeableConcept, Task } from '@medplum/fhirtypes';
import type { ValueSetCode } from '../components/records/ValueSetCombobox';
import { LOINC_SYSTEM } from '../utils/observation';

/** Spanish translation for a code, when the app language is Spanish and a translation exists. */
function esDisplayFor(code: CodeableConcept | undefined): string | undefined {
  const loincCode = code?.coding?.find((c) => c.system === LOINC_SYSTEM)?.code;
  if (loincCode && LOINC_DISPLAY_NAMES_ES[loincCode]) return LOINC_DISPLAY_NAMES_ES[loincCode];
  const surveyCode = code?.coding?.find((c) => c.system === SURVEY_SCORES_SYSTEM)?.code;
  if (surveyCode && SURVEY_SCORES_DISPLAY_NAMES_ES[surveyCode]) return SURVEY_SCORES_DISPLAY_NAMES_ES[surveyCode];
  return undefined;
}

/**
 * Language-aware display helpers for clinical codes. Owns the language detection so no component
 * needs to import dictionaries, check i18n.language, or handle regional variants (es-MX, etc.)
 * directly. Add a language here and all consumers update automatically.
 */
export function useCodeDisplay(): {
  /** The resolved app language (`'en'` | `'es'`). Exposed so callers can localize strings this hook
   * does not resolve for them — e.g. pass it straight to `getLifestyleMeasurementDisplay(code, lang)`
   * when the lifestyle picker builds its own item labels. */
  lang: 'en' | 'es';
  /** Translated display name for a Task's result code, falling back to task.description. */
  displayForTask: (task: Task) => string;
  /** Remap ValueSet items to translated display names (surveys in the measurement picker). */
  translateItems: (items: ValueSetCode[]) => ValueSetCode[];
  /**
   * Best display name for an observation code: Spanish translation when the app language starts
   * with 'es', curated English label otherwise, then the coding's own text/display, then the raw
   * code, then ''. Replaces getLabDisplayName(code, i18n.language) at render sites.
   *
   * Pass `{ fallbackToCode: false }` to omit the raw-code fallback (returns '' instead) so callers
   * with a better name source — e.g. a Task display — can fall through to it rather than showing
   * the LOINC code.
   */
  displayFor: (code: CodeableConcept | undefined, options?: { fallbackToCode?: boolean }) => string;
  /**
   * Localized unit for a lifestyle-measurement LOINC code, or `undefined` for any other code — so
   * callers can `unitFor(code) ?? <persisted English unit>` and leave clinical vitals untouched.
   */
  unitFor: (code: string | undefined) => string | undefined;
  /**
   * Display name for a row keyed on a bare result code. Prefers the curated/translated label for the
   * key itself, so a row merging variant-coded readings keeps one label as readings arrive; a code
   * with no curated label takes `fallback`'s own text/display (typically the newest reading's code),
   * then the raw code.
   */
  displayForResultCode: (code: string | undefined, fallback?: CodeableConcept) => string;
} {
  const { i18n } = useTranslation();
  const isEs = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es');
  const lang = isEs ? 'es' : 'en';

  const displayForTask = useCallback(
    (task: Task): string => {
      if (isEs) {
        const esName = getTaskResultDisplayNameEs(task);
        if (esName) return esName;
        const loincCode = getTaskLoincCode(task);
        const esLifestyle = loincCode ? getLifestyleMeasurementDisplay(loincCode, lang) : undefined;
        if (esLifestyle) return esLifestyle;
      }
      return task.description ?? '';
    },
    [isEs, lang],
  );

  const translateItems = useCallback(
    (items: ValueSetCode[]): ValueSetCode[] => {
      if (!isEs) return items;
      return items.map((item) => {
        const esDisplay = SURVEY_SCORES_DISPLAY_NAMES_ES[item.code] ?? LOINC_DISPLAY_NAMES_ES[item.code];
        return esDisplay ? { ...item, display: esDisplay } : item;
      });
    },
    [isEs],
  );

  const displayFor = useCallback(
    (code: CodeableConcept | undefined, options?: { fallbackToCode?: boolean }): string => {
      if (isEs) {
        const esLabel = esDisplayFor(code);
        if (esLabel) return esLabel;
      }
      const loincCode = code?.coding?.find((c) => c.system === LOINC_SYSTEM)?.code;
      const friendly = loincCode ? getLoincLabDisplayName(loincCode) : undefined;
      if (friendly) return friendly;
      // Lifestyle-measurement catalog names (localized). Wins over the coding's own text/display and
      // the raw-code fallback; `undefined` for non-lifestyle codes leaves clinical labels untouched.
      const lifestyle = loincCode ? getLifestyleMeasurementDisplay(loincCode, lang) : undefined;
      if (lifestyle) return lifestyle;
      // No curated/translated label. `formatCodeableConcept` returns the coding's text/display, but
      // falls back to the raw code when neither exists — omit that when the caller has a better name.
      if (options?.fallbackToCode === false) {
        // Deliberately scan every coding for a display rather than mirroring `formatCodeableConcept`'s
        // coding-order handling: the goal here is to surface any human-readable name (e.g. a local-system
        // coding on an EHR-imported panel whose LOINC coding has none), and otherwise return '' so the
        // caller can fall through to its own name source instead of a bare code.
        return code?.text ?? code?.coding?.find((c) => c.display)?.display ?? '';
      }
      return formatCodeableConcept(code) ?? '';
    },
    [isEs, lang],
  );

  const displayForResultCode = useCallback(
    (code: string | undefined, fallback?: CodeableConcept): string => {
      if (!code) return '';
      return (
        displayFor({ coding: [{ system: LOINC_SYSTEM, code }] }, { fallbackToCode: false }) ||
        displayFor(fallback, { fallbackToCode: false }) ||
        code
      );
    },
    [displayFor],
  );

  const unitFor = useCallback(
    (code: string | undefined): string | undefined => (code ? getLifestyleMeasurementUnit(code, lang) : undefined),
    [lang],
  );

  return { lang, displayForTask, translateItems, displayFor, displayForResultCode, unitFor };
}
